import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { apiClientV2 } from "@/services/api/apiClient"
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import {
  type EmailCopy,
  type EmailTemplatesByLang,
  type InquiryTemplateSettings,
  type TemplateLogo,
} from "../components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { getApiErrorCode, toastApiError } from "./apiError"

// Org-level branding + signature + email cover-message templates.
// Endpoint always returns a complete, sendable set (gaps filled with
// placeholder defaults), so the composer is never blocked.
const BASE = "/material-compliance/inquiry-template-settings"

const QUERY_KEY = ["mc", "inquiries", "template-settings"]

/** Scope guards (WP1). Mirrors the backend's DEFAULT_SCOPE_*_SUBJECTS; used
 *  wherever the stored settings have not loaded yet. */
export const DEFAULT_SCOPE_WARN_SUBJECTS = 500
export const DEFAULT_SCOPE_MAX_SUBJECTS = 2000

interface WireEmailContent {
  subject: string
  body: string
}

/** Authoritative wire shape (post camelCase conversion). */
interface InquiryTemplateSettingsWire {
  settingsStatus: "unconfigured" | "configured"
  logoUrl: string // resolved data-uri / https url; transparent 1×1 until set
  footer: string // rendered on the PDF questionnaire
  emailSignatures: Record<string, string> // per-language sign-off {lang: str} appended below the email body
  contactPerson: string // org's inquiry contact → questionnaire PDF cover "Ansprechpartner"
  emailTemplates: Record<string, Record<string, WireEmailContent>>
  defaultCc: string[] // Cc addresses seeded into every new draft
  defaultAttachmentFormat: string // pdf | excel | pdf_and_excel — seeds new drafts
  attachmentFormatLocked: boolean // enforce the default; composer hides the choice
  activeLanguages: string[]
  defaultLanguage: string
  accentColor: string | null
  remindersEnabled: boolean
  reminderIntervalDays: number
  maxReminders: number
  defaultDueDays: number // org-wide response deadline seeded into every new draft
  declarationFreshnessDays: number
  printValidUntil: boolean
  validityDays: number
  place: string | null
  productGroupLabel: string | null
  /** Scope guards (WP1): warn the editor above the first, refuse generate above
   *  the second. Server defaults are 500 / 2000. */
  scopeWarnSubjects?: number
  scopeMaxSubjects?: number
}

function resolveLogo(logoUrl: string): TemplateLogo | null {
  if (!logoUrl) return null
  if (logoUrl.startsWith("data:") && logoUrl.length < 512) return null
  return { url: logoUrl }
}

// One copy block from the wire map, resilient to a missing stage/lang (the backend
// returns a complete merged map, so this only guards a future gap): DE falls back to EN.
function wireCopy(
  wire: InquiryTemplateSettingsWire,
  stage: "first" | "reminder",
  lang: "en" | "de",
): WireEmailContent {
  const c =
    wire.emailTemplates?.[stage]?.[lang] ?? wire.emailTemplates?.[stage]?.en
  return { subject: c?.subject ?? "", body: c?.body ?? "" }
}

function toEmailTemplates(
  wire: InquiryTemplateSettingsWire,
): EmailTemplatesByLang {
  return {
    first: {
      en: wireCopy(wire, "first", "en"),
      de: wireCopy(wire, "first", "de"),
    },
    reminder: {
      en: wireCopy(wire, "reminder", "en"),
      de: wireCopy(wire, "reminder", "de"),
    },
  }
}

/**
 * A partial edit of the template grid — any stage, language, or half. The dialog
 * hands over the whole grid; the agent chat changes one cell.
 */
export type EmailTemplatesPatch = {
  [S in keyof EmailTemplatesByLang]?: {
    [L in keyof EmailTemplatesByLang[S]]?: Partial<EmailCopy>
  }
}

// Overlay onto the full wire map, preserving every stage, language and half the
// patch doesn't mention — including keys the UI never edits (`final`, and the
// reminder subject the backend forces to "Re: <original>").
export function mergeEmailTemplates(
  base: Record<string, Record<string, WireEmailContent>> | undefined,
  patch: EmailTemplatesPatch,
): Record<string, Record<string, WireEmailContent>> {
  const out: Record<string, Record<string, WireEmailContent>> = {}
  for (const [stage, byLang] of Object.entries(base ?? {})) {
    out[stage] = { ...byLang }
  }
  for (const [stage, byLang] of Object.entries(patch)) {
    out[stage] = { ...(out[stage] ?? {}) }
    for (const [lang, copy] of Object.entries(byLang ?? {})) {
      const current = out[stage][lang] ?? { subject: "", body: "" }
      out[stage][lang] = {
        subject: copy?.subject ?? current.subject,
        body: copy?.body ?? current.body,
      }
    }
  }
  return out
}

export function toUiSettings(
  wire: InquiryTemplateSettingsWire,
): InquiryTemplateSettings {
  return {
    logo: resolveLogo(wire.logoUrl),
    questionnaireFooter: wire.footer,
    defaultLanguage: wire.defaultLanguage,
    emailSignatures: {
      en: wire.emailSignatures?.en ?? "",
      de: wire.emailSignatures?.de ?? "",
    },
    contactPerson: wire.contactPerson ?? "",
    defaultCc: wire.defaultCc ?? [],
    defaultAttachmentFormat:
      wire.defaultAttachmentFormat === "excel" ||
      wire.defaultAttachmentFormat === "pdf_and_excel"
        ? wire.defaultAttachmentFormat
        : "pdf",
    attachmentFormatLocked: wire.attachmentFormatLocked ?? false,
    remindersEnabled: wire.remindersEnabled,
    reminderIntervalDays: wire.reminderIntervalDays,
    maxReminders: wire.maxReminders,
    defaultDueDays: wire.defaultDueDays,
    declarationFreshnessDays: wire.declarationFreshnessDays ?? 365,
    printValidUntil: wire.printValidUntil ?? false,
    validityDays: wire.validityDays ?? 365,
    place: wire.place ?? "",
    productGroupLabel: wire.productGroupLabel ?? "",
    scopeWarnSubjects: wire.scopeWarnSubjects ?? DEFAULT_SCOPE_WARN_SUBJECTS,
    scopeMaxSubjects: wire.scopeMaxSubjects ?? DEFAULT_SCOPE_MAX_SUBJECTS,
    emailTemplates: toEmailTemplates(wire),
  }
}

async function fetchWire(): Promise<InquiryTemplateSettingsWire> {
  return apiClientV2.get<InquiryTemplateSettingsWire>(BASE).then((r) => r.data)
}

/** The org's inquiry settings query, also for awaiting them outside a
 *  component (`queryClient.ensureQueryData`). */
export const templateSettingsQuery = {
  queryKey: QUERY_KEY,
  queryFn: async (): Promise<InquiryTemplateSettings> =>
    toUiSettings(await fetchWire()),
}

export function useTemplateSettings(enabled = true) {
  return useQuery<InquiryTemplateSettings>({
    ...templateSettingsQuery,
    enabled,
  })
}

export type TemplateSettingsPatch = Partial<
  Pick<
    InquiryTemplateSettings,
    | "questionnaireFooter"
    | "contactPerson"
    | "defaultCc"
    | "defaultAttachmentFormat"
    | "attachmentFormatLocked"
    | "remindersEnabled"
    | "reminderIntervalDays"
    | "maxReminders"
    | "defaultDueDays"
    | "declarationFreshnessDays"
    | "printValidUntil"
    | "validityDays"
    | "place"
    | "defaultLanguage"
    | "productGroupLabel"
    | "scopeWarnSubjects"
    | "scopeMaxSubjects"
  >
> & {
  /** Partial per stage/language/half — see mergeEmailTemplates. */
  emailTemplates?: EmailTemplatesPatch
  /**
   * Per-language sign-off. PARTIAL is allowed — it is merged onto the stored
   * value per language key, so sending only "de" leaves "en" alone.
   */
  emailSignatures?: Partial<InquiryTemplateSettings["emailSignatures"]>
}

/**
 * Apply a partial change to the org's inquiry template settings.
 *
 * The endpoint is a FULL upsert, and six of its fields — footer, contactPerson,
 * emailTemplates, activeLanguages, defaultLanguage, accentColor — are written
 * unconditionally from whatever the request carried. A PUT that omits them
 * therefore blanks the org's footer, contact person and every customized email
 * template, and the next GET still returns built-in defaults, so the loss is
 * invisible. Every caller must go through this function.
 *
 * The base is fetched fresh rather than read from cache, so a save always merges
 * onto current server state — a stale base would silently revert any edit made
 * since the last fetch (another tab, another admin, a re-login into another org).
 */
export async function saveTemplateSettings(
  patch: TemplateSettingsPatch,
): Promise<InquiryTemplateSettings> {
  const base = await fetchWire()
  const body = {
    footer: patch.questionnaireFooter ?? base.footer,
    // Merged per language, exactly like emailTemplates below. A caller may
    // legitimately send one language (the agent chat does), and the backend writes
    // this blob verbatim — so a whole-object replace would revert the org's other
    // language to the built-in default, invisibly.
    emailSignatures: {
      ...(base.emailSignatures ?? {}),
      ...(patch.emailSignatures ?? {}),
    },
    contactPerson: patch.contactPerson ?? base.contactPerson,
    defaultCc: patch.defaultCc ?? base.defaultCc ?? [],
    defaultAttachmentFormat:
      patch.defaultAttachmentFormat ?? base.defaultAttachmentFormat ?? "pdf",
    attachmentFormatLocked:
      patch.attachmentFormatLocked ?? base.attachmentFormatLocked ?? false,
    // Overlay edited templates onto the fresh base so other stages/langs aren't dropped.
    emailTemplates: patch.emailTemplates
      ? mergeEmailTemplates(base.emailTemplates, patch.emailTemplates)
      : base.emailTemplates,
    // Patchable, not pinned to `base`: every PUT re-sends this field, and the GET
    // falls back to 'en' when the org has no row — so hard-wiring it meant the first
    // save of ANY setting froze the org's document language to English (F11).
    // The org's OWN active languages come first: rebuilding the list from the
    // default alone silently dropped every other one, so an org running de+en
    // that switched its default to en lost German from a full-upsert payload.
    activeLanguages: patch.defaultLanguage
      ? Array.from(
          new Set([
            ...(base.activeLanguages ?? []),
            patch.defaultLanguage,
            "en",
          ]),
        )
      : base.activeLanguages,
    defaultLanguage: patch.defaultLanguage ?? base.defaultLanguage,
    accentColor: base.accentColor,
    remindersEnabled: patch.remindersEnabled ?? base.remindersEnabled,
    reminderIntervalDays:
      patch.reminderIntervalDays ?? base.reminderIntervalDays,
    maxReminders: patch.maxReminders ?? base.maxReminders,
    defaultDueDays: patch.defaultDueDays ?? base.defaultDueDays,
    declarationFreshnessDays:
      patch.declarationFreshnessDays ?? base.declarationFreshnessDays,
    printValidUntil: patch.printValidUntil ?? base.printValidUntil,
    validityDays: patch.validityDays ?? base.validityDays,
    place: patch.place ?? base.place,
    productGroupLabel: patch.productGroupLabel ?? base.productGroupLabel,
    scopeWarnSubjects: patch.scopeWarnSubjects ?? base.scopeWarnSubjects,
    scopeMaxSubjects: patch.scopeMaxSubjects ?? base.scopeMaxSubjects,
  }
  const wire = await apiClientV2
    .put<InquiryTemplateSettingsWire>(BASE, body)
    .then((r) => r.data)
  return toUiSettings(wire)
}

/**
 * Put saved settings into the cache every settings surface reads. Shared with the
 * agent chat, which calls saveTemplateSettings directly: without it the dialog
 * shows the pre-save values until a reload.
 */
export function syncTemplateSettingsCache(
  qc: QueryClient,
  next: InquiryTemplateSettings,
): void {
  qc.setQueryData(QUERY_KEY, next)
}

/** Persists the questionnaire footer, email signature, cadence, and/or email templates. */
export function useUpdateTemplateSettings() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: saveTemplateSettings,
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
    },
    onError: (error) => {
      // Surface a save failure (e.g. a 422 validation error) rather than letting the
      // button silently re-enable with no feedback. Never the server's own words,
      // the UI never shows backend prose, so a fixed, translated line either way.
      toastApiError(error, translate, "MC_TPL_SAVE_ERR")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// Maps the backend logo-error codes to a translated message; null for any
// other code, so the caller's own fallback applies. Shared with the
// organization logo upload in settings, which answers with the same codes.
export function logoErrorKey(code: string | undefined): TranslationKeys | null {
  switch (code) {
    case "LOGO_TOO_LARGE":
      return "MC_TPL_LOGO_ERR_TOO_LARGE"
    case "INVALID_LOGO_TYPE":
      return "MC_TPL_LOGO_ERR_TYPE"
    default:
      return null
  }
}

/** Uploads a replacement logo (multipart) and returns its resolved metadata. */
export function useUploadTemplateLogo() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (file: File): Promise<TemplateLogo> => {
      const form = new FormData()
      form.append("file", file)
      const { logoUrl } = await apiClientV2
        .post<{ logoUrl: string }>(`${BASE}/logo`, form)
        .then((r) => r.data)
      // The endpoint returns only the resolved url; derive display metadata
      // from the just-uploaded file for the session.
      return {
        url: logoUrl,
        fileName: file.name,
        meta: `${(file.type.split("/")[1] ?? "file").toUpperCase()} · uploaded just now`,
      }
    },
    onSuccess: (logo) => {
      queryClient.setQueryData<InquiryTemplateSettings>(QUERY_KEY, (prev) =>
        prev ? { ...prev, logo } : prev,
      )
    },
    onError: (error) => {
      const key = logoErrorKey(getApiErrorCode(error) ?? undefined)
      toastApiError(error, translate, key ?? "MC_TPL_LOGO_ERR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

export function useRemoveTemplateLogo() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async () => {
      await apiClientV2.delete(`${BASE}/logo`)
    },
    onSuccess: () => {
      queryClient.setQueryData<InquiryTemplateSettings>(QUERY_KEY, (prev) =>
        prev ? { ...prev, logo: null } : prev,
      )
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}
